import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Flex, Text, Spinner } from '@chakra-ui/react'
import { LuRefreshCw } from 'react-icons/lu'
import { miningRightsService } from '../../../api/services'
import type { MiningRightMapItemResponse } from '../../../api/models/response/miningRight'
import { useT } from '../../../i18n'
import { ComponentesMapView } from './ComponentesMapView'
import { SearchToolbar } from './SearchToolbar'
import { DmDetailDrawer } from './DmDetailDrawer'

function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

export default function ComponentesMinerosPage() {
  const { t } = useT()
  const [items, setItems] = useState<MiningRightMapItemResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await miningRightsService.getMap()
      setItems(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchData() }, [fetchData])

  const filtered = useMemo(() => {
    const q = normalize(search.trim())
    if (!q) return items
    return items.filter((it) => {
      const haystack = normalize(`${it.name} ${it.uniqueCode} ${it.minerName}`)
      return haystack.includes(q)
    })
  }, [items, search])

  const selectedDm = useMemo(
    () => items.find((it) => it.id === selectedId) ?? null,
    [items, selectedId],
  )

  return (
    <Box position="relative" h="calc(100vh - 120px)" display="flex" flexDirection="column" gap={4}>
      <Flex alignItems="center" justifyContent="space-between" flexShrink={0}>
        <Box>
          <Text fontSize="2xl" fontWeight="800">{t('origen.componentes.title')}</Text>
          <Text fontSize="sm" color="#857b71">{t('origen.componentes.subtitle')}</Text>
        </Box>
        <Flex
          as="button"
          type="button"
          alignItems="center"
          gap={2}
          px={4}
          py={2}
          borderRadius="md"
          bg="#b4532a"
          color="white"
          fontSize="sm"
          fontWeight="600"
          onClick={() => void fetchData()}
          disabled={loading}
        >
          <LuRefreshCw size={14} />
          {t('origen.componentes.retry')}
        </Flex>
      </Flex>

      <SearchToolbar
        value={search}
        onChange={setSearch}
        showNoResults={!loading && !error && filtered.length === 0 && search.trim().length > 0}
      />

      <Box flex="1" position="relative" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
        {loading && items.length === 0 ? (
          <Flex h="100%" alignItems="center" justifyContent="center">
            <Spinner />
          </Flex>
        ) : error ? (
          <Flex h="100%" alignItems="center" justifyContent="center" direction="column" gap={2}>
            <Text color="red.600" fontWeight="600">{t('origen.componentes.loadError')}</Text>
            <Text fontSize="xs" color="gray.500" maxW="400px" textAlign="center">{error}</Text>
            <Flex
              as="button"
              type="button"
              alignItems="center"
              gap={2}
              px={3}
              py={2}
              borderRadius="md"
              bg="#b4532a"
              color="white"
              fontSize="sm"
              fontWeight="600"
              mt={2}
              onClick={() => void fetchData()}
              disabled={loading}
            >
              {t('origen.componentes.retry')}
            </Flex>
          </Flex>
        ) : (
          <ComponentesMapView items={filtered} onSelect={setSelectedId} />
        )}
      </Box>

      <DmDetailDrawer dm={selectedDm} onClose={() => setSelectedId(null)} />
    </Box>
  )
}
